import React, { useState, useRef, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import Layout from '../components/Layout';
import ResponsiveTableWrapper from '../components/ResponsiveTableWrapper';
import * as pushNotificationService from '../api/pushNotificationService';
import { userService } from '../api/userService';
import type { User } from '../api/userService';

interface NotificationForm {
  title: string;
  body: string;
  targetType: 'all' | 'users' | 'roles';
  selectedUserIds: number[];
  roles: string;
  dataJson: string;
}

const PushNotifications: React.FC = () => {
  const queryClient = useQueryClient();
  
  const [form, setForm] = useState<NotificationForm>({
    title: '',
    body: '',
    targetType: 'all',
    selectedUserIds: [],
    roles: '',
    dataJson: '',
  });

  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');
  const [deleteConfirmId, setDeleteConfirmId] = useState<number | null>(null);

  const [userSearch, setUserSearch] = useState('');
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const userDropdownRef = useRef<HTMLDivElement>(null);

  const { data: usersData } = useQuery({
    queryKey: ['all-users-for-push', userSearch],
    queryFn: () => userService.getAllUsers({ search: userSearch, limit: 50, isActive: true }),
    enabled: form.targetType === 'users',
  });

  const allUsers: User[] = usersData?.data?.users || usersData?.data || [];
  const filteredUsers = allUsers.filter((u: User) => !form.selectedUserIds.includes(u.id));

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (userDropdownRef.current && !userDropdownRef.current.contains(event.target as Node)) {
        setShowUserDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const addUser = (user: User) => {
    if (!form.selectedUserIds.includes(user.id)) {
      setForm({ ...form, selectedUserIds: [...form.selectedUserIds, user.id] });
    }
    setUserSearch('');
    setShowUserDropdown(false);
  };

  const removeUser = (userId: number) => {
    setForm({ ...form, selectedUserIds: form.selectedUserIds.filter(id => id !== userId) });
  };

  const getSelectedUserName = (userId: number): string => {
    const user = allUsers.find((u: User) => u.id === userId);
    return user ? `${user.firstName} ${user.lastName}` : `User #${userId}`;
  };

  // Fetch stats
  const { data: statsData } = useQuery({
    queryKey: ['push-notification-stats'],
    queryFn: () => pushNotificationService.getStats(),
  });

  // Fetch history
  const { data: historyData, isLoading: historyLoading } = useQuery({
    queryKey: ['push-notification-history'],
    queryFn: () => pushNotificationService.getNotificationHistory(20, 0),
  });

  // Send notification mutation
  const sendMutation = useMutation({
    mutationFn: async (formData: NotificationForm) => {
      const payload: any = {
        title: formData.title,
        body: formData.body,
      };

      // Parse custom data if provided
      if (formData.dataJson) {
        try {
          payload.data = JSON.parse(formData.dataJson);
        } catch (e) {
          throw new Error('Invalid JSON in custom data field');
        }
      }

      switch (formData.targetType) {
        case 'all':
          return pushNotificationService.sendToAll(payload);
        case 'users':
          if (formData.selectedUserIds.length === 0) {
            throw new Error('Please select at least one user');
          }
          return pushNotificationService.sendToUsers({ ...payload, userIds: formData.selectedUserIds });
        case 'roles': {
          const roles = formData.roles.split(',').map(r => r.trim()).filter(r => r);
          if (roles.length === 0) {
            throw new Error('Please provide valid roles');
          }
          return pushNotificationService.sendToRoles({ ...payload, roles });
        }
        default:
          throw new Error('Invalid target type');
      }
    },
    onSuccess: (data) => {
      setSuccess(`Notification sent successfully! ${data.data.successfulSends}/${data.data.totalRecipients} delivered`);
      setError('');
      setForm({
        title: '',
        body: '',
        targetType: 'all',
        selectedUserIds: [],
        roles: '',
        dataJson: '',
      });
      queryClient.invalidateQueries({ queryKey: ['push-notification-history'] });
      queryClient.invalidateQueries({ queryKey: ['push-notification-stats'] });
    },
    onError: (error: any) => {
      setError(error.response?.data?.message || error.message || 'Failed to send notification');
      setSuccess('');
    },
  });

  // Delete notification mutation
  const deleteMutation = useMutation({
    mutationFn: (id: number) => pushNotificationService.deleteNotification(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['push-notification-history'] });
      queryClient.invalidateQueries({ queryKey: ['push-notification-stats'] });
      setSuccess('Notification deleted successfully!');
      setError('');
      setDeleteConfirmId(null);
    },
    onError: (error: any) => {
      setError(error.response?.data?.message || 'Failed to delete notification');
      setSuccess('');
      setDeleteConfirmId(null);
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!form.title || !form.body) {
      setError('Title and body are required');
      return;
    }

    sendMutation.mutate(form);
  };

  const handleDelete = (id: number) => {
    setDeleteConfirmId(id);
  };

  const confirmDelete = () => {
    if (deleteConfirmId) {
      deleteMutation.mutate(deleteConfirmId);
    }
  };

  const cancelDelete = () => {
    setDeleteConfirmId(null);
  };

  const stats = statsData?.data;

  return (
    <Layout>
      <div className="px-4 sm:px-6 lg:px-8 py-8">
        <div className="sm:flex sm:items-center">
          <div className="sm:flex-auto">
            <h1 className="text-3xl font-bold text-gray-900">Push Notifications</h1>
            <p className="mt-2 text-sm text-gray-700">
              Send push notifications to iOS app users
            </p>
          </div>
        </div>

        {/* Statistics */}
        {stats && (
          <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
            <div className="bg-white overflow-hidden shadow rounded-lg">
              <div className="p-5">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <svg className="h-6 w-6 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
                    </svg>
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-gray-500 truncate">Active Devices</dt>
                      <dd className="text-2xl font-semibold text-gray-900">{stats.devices.active}</dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white overflow-hidden shadow rounded-lg">
              <div className="p-5">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <svg className="h-6 w-6 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                    </svg>
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-gray-500 truncate">Sent (30 days)</dt>
                      <dd className="text-2xl font-semibold text-gray-900">{stats.notifications.last30Days}</dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white overflow-hidden shadow rounded-lg">
              <div className="p-5">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <svg className="h-6 w-6 text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-gray-500 truncate">Successful</dt>
                      <dd className="text-2xl font-semibold text-green-600">{stats.notifications.successfulSends}</dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white overflow-hidden shadow rounded-lg">
              <div className="p-5">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <svg className="h-6 w-6 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-gray-500 truncate">Failed</dt>
                      <dd className="text-2xl font-semibold text-red-600">{stats.notifications.failedSends}</dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Send Notification Form */}
        <div className="mt-8 bg-white shadow rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <h3 className="text-lg leading-6 font-medium text-gray-900">
              Send New Notification
            </h3>
            
            <form onSubmit={handleSubmit} className="mt-5 space-y-6">
              {error && (
                <div className="rounded-md bg-red-50 p-4">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <p className="text-sm text-red-800">{error}</p>
                    </div>
                  </div>
                </div>
              )}

              {success && (
                <div className="rounded-md bg-green-50 p-4">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <svg className="h-5 w-5 text-green-400" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <p className="text-sm text-green-800">{success}</p>
                    </div>
                  </div>
                </div>
              )}

              <div>
                <label htmlFor="title" className="block text-sm font-medium text-gray-700">
                  Title *
                </label>
                <input
                  type="text"
                  id="title"
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                  placeholder="Notification title"
                  required
                />
              </div>

              <div>
                <label htmlFor="body" className="block text-sm font-medium text-gray-700">
                  Message *
                </label>
                <textarea
                  id="body"
                  rows={3}
                  value={form.body}
                  onChange={(e) => setForm({ ...form, body: e.target.value })}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                  placeholder="Notification message"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Send To
                </label>
                <div className="mt-2 space-y-4">
                  <div className="flex items-center">
                    <input
                      id="targetAll"
                      type="radio"
                      value="all"
                      checked={form.targetType === 'all'}
                      onChange={(e) => setForm({ ...form, targetType: e.target.value as any })}
                      className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300"
                    />
                    <label htmlFor="targetAll" className="ml-3 block text-sm font-medium text-gray-700">
                      All Users
                    </label>
                  </div>

                  <div className="flex items-center">
                    <input
                      id="targetUsers"
                      type="radio"
                      value="users"
                      checked={form.targetType === 'users'}
                      onChange={(e) => setForm({ ...form, targetType: e.target.value as any })}
                      className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300"
                    />
                    <label htmlFor="targetUsers" className="ml-3 block text-sm font-medium text-gray-700">
                      Specific Users
                    </label>
                  </div>

                  {form.targetType === 'users' && (
                    <div className="ml-7" ref={userDropdownRef}>
                      {form.selectedUserIds.length > 0 && (
                        <div className="flex flex-wrap gap-2 mb-2">
                          {form.selectedUserIds.map((userId) => (
                            <span
                              key={userId}
                              className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-sm font-medium bg-primary-100 text-primary-800"
                            >
                              {getSelectedUserName(userId)}
                              <button
                                type="button"
                                onClick={() => removeUser(userId)}
                                className="flex-shrink-0 ml-0.5 h-4 w-4 rounded-full inline-flex items-center justify-center text-primary-500 hover:bg-primary-200 hover:text-primary-600 focus:outline-none"
                              >
                                <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                              </button>
                            </span>
                          ))}
                        </div>
                      )}
                      <div className="relative">
                        <input
                          type="text"
                          value={userSearch}
                          onChange={(e) => {
                            setUserSearch(e.target.value);
                            setShowUserDropdown(true);
                          }}
                          onFocus={() => setShowUserDropdown(true)}
                          className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                          placeholder="Search users by name or email..."
                        />
                        {showUserDropdown && (
                          <div className="absolute z-10 mt-1 w-full bg-white shadow-lg max-h-60 rounded-md py-1 text-base ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none sm:text-sm">
                            {filteredUsers.length > 0 ? (
                              filteredUsers.map((user: User) => (
                                <button
                                  key={user.id}
                                  type="button"
                                  onClick={() => addUser(user)}
                                  className="w-full text-left px-4 py-2 hover:bg-primary-50 flex items-center justify-between"
                                >
                                  <div>
                                    <div className="text-sm font-medium text-gray-900">
                                      {user.firstName} {user.lastName}
                                    </div>
                                    <div className="text-xs text-gray-500">{user.email}</div>
                                  </div>
                                  {user.roles && user.roles.length > 0 && (
                                    <div className="flex gap-1 ml-2">
                                      {user.roles.map((role) => (
                                        <span key={role.id} className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600">
                                          {role.name}
                                        </span>
                                      ))}
                                    </div>
                                  )}
                                </button>
                              ))
                            ) : (
                              <div className="px-4 py-3 text-sm text-gray-500">
                                {userSearch ? 'No users found' : 'Type to search users...'}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                      <p className="mt-1 text-xs text-gray-500">
                        {form.selectedUserIds.length} user{form.selectedUserIds.length !== 1 ? 's' : ''} selected
                      </p>
                    </div>
                  )}

                  <div className="flex items-center">
                    <input
                      id="targetRoles"
                      type="radio"
                      value="roles"
                      checked={form.targetType === 'roles'}
                      onChange={(e) => setForm({ ...form, targetType: e.target.value as any })}
                      className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300"
                    />
                    <label htmlFor="targetRoles" className="ml-3 block text-sm font-medium text-gray-700">
                      By Role
                    </label>
                  </div>

                  {form.targetType === 'roles' && (
                    <input
                      type="text"
                      value={form.roles}
                      onChange={(e) => setForm({ ...form, roles: e.target.value })}
                      className="ml-7 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                      placeholder="hub_leader, coach"
                    />
                  )}
                </div>
              </div>

              <div>
                <label htmlFor="dataJson" className="block text-sm font-medium text-gray-700">
                  Custom Data (JSON)
                </label>
                <textarea
                  id="dataJson"
                  rows={3}
                  value={form.dataJson}
                  onChange={(e) => setForm({ ...form, dataJson: e.target.value })}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm font-mono text-xs"
                  placeholder='{"type": "planter_update", "planterId": 123}'
                />
                <p className="mt-1 text-xs text-gray-500">
                  Optional custom data for the notification (must be valid JSON)
                </p>
              </div>

              <div className="flex justify-end">
                <button
                  type="submit"
                  disabled={sendMutation.isPending}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50"
                >
                  {sendMutation.isPending ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                      Sending...
                    </>
                  ) : (
                    <>
                      <svg className="-ml-1 mr-2 h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                      </svg>
                      Send Notification
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>

        {/* Notification History */}
        <div className="mt-8 bg-white shadow rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
              Recent Notifications
            </h3>

            {historyLoading ? (
              <div className="text-center py-4">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
              </div>
            ) : historyData?.data && historyData.data.length > 0 ? (
              <ResponsiveTableWrapper>
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider sm:px-6">
                        Title
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider sm:px-6">
                        Target
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider sm:px-6">
                        Recipients
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider sm:px-6">
                        Status
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider sm:px-6">
                        Sent By
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider sm:px-6">
                        Date
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider sm:px-6">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {historyData.data.map((notification) => (
                      <tr key={notification.id}>
                        <td className="px-4 py-4 whitespace-normal sm:px-6 sm:whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">{notification.title}</div>
                          <div className="text-sm text-gray-500 truncate max-w-xs">{notification.body}</div>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap sm:px-6">
                          <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800">
                            {notification.targetType}
                          </span>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500 sm:px-6">
                          {notification.totalRecipients}
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap sm:px-6">
                          <div className="text-sm text-green-600">{notification.successfulSends} sent</div>
                          {notification.failedSends > 0 && (
                            <div className="text-sm text-red-600">{notification.failedSends} failed</div>
                          )}
                        </td>
                        <td className="px-4 py-4 whitespace-normal text-sm text-gray-500 sm:px-6 sm:whitespace-nowrap">
                          {notification.sentBy ? `${notification.sentBy.firstName} ${notification.sentBy.lastName}` : 'Unknown'}
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500 sm:px-6">
                          {new Date(notification.sentAt).toLocaleString()}
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-sm font-medium sm:px-6">
                          <button
                            onClick={() => handleDelete(notification.id)}
                            className="text-red-600 hover:text-red-900 transition-colors"
                            disabled={deleteMutation.isPending}
                          >
                            {deleteMutation.isPending && deleteConfirmId === notification.id ? 'Deleting...' : 'Delete'}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </ResponsiveTableWrapper>
            ) : (
              <p className="text-gray-500 text-center py-4">No notifications sent yet</p>
            )}
          </div>
        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      {deleteConfirmId && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3 text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100">
                <svg className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900 mt-4">Delete Notification</h3>
              <div className="mt-2 px-7 py-3">
                <p className="text-sm text-gray-500">
                  Are you sure you want to delete this notification? This action cannot be undone.
                </p>
              </div>
              <div className="items-center px-4 py-3">
                <button
                  onClick={confirmDelete}
                  disabled={deleteMutation.isPending}
                  className="px-4 py-2 bg-red-600 text-white text-base font-medium rounded-md w-24 mr-2 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-300 disabled:opacity-50"
                >
                  {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
                </button>
                <button
                  onClick={cancelDelete}
                  disabled={deleteMutation.isPending}
                  className="px-4 py-2 bg-gray-300 text-gray-800 text-base font-medium rounded-md w-24 hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-300 disabled:opacity-50"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default PushNotifications;

